import React, { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import AddCategoryGroupDialog from './AddCategoryGroupDialog';
import './CategoryGroups.css';

function CategoryGroups() {
  const [accountGroups, setAccountGroups] = useState([]);
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const user = auth.currentUser;

  // Load account groups
  useEffect(() => {
    if (!user) return;
    const accountGroupsRef = collection(db, 'users', user.uid, 'account_groups');
    const unsubscribe = onSnapshot(accountGroupsRef, (snapshot) => {
      const accountGroupsData = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        source: 'account'
      }));
      // Sort alphabetically by name
      accountGroupsData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setAccountGroups(accountGroupsData);
    });
    return unsubscribe;
  }, [user]);

  // Load category groups
  useEffect(() => {
    if (!user) return;
    const categoryGroupsRef = collection(db, 'users', user.uid, 'category_groups');
    const unsubscribe = onSnapshot(categoryGroupsRef, (snapshot) => {
      const categoryGroupsData = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        source: 'category'
      }));
      // Sort alphabetically by name
      categoryGroupsData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCategoryGroups(categoryGroupsData);
    });
    return unsubscribe;
  }, [user]);

  const handleEdit = (group) => {
    setEditingGroup(group);
    setShowAddDialog(true);
  };

  const renderGroupTable = (groups, sourceType) => {
    if (groups.length === 0) return null;

    return (
      <div className="group-section" key={sourceType}>
        <div className="group-section-header">
          <h3>{sourceType === 'account' ? '🏦 Account Groups' : '📁 Category Groups'}</h3>
          <span className="group-count">{groups.length} groups</span>
        </div>
        
        <table className="category-groups-table">
          <thead>
            <tr>
              <th>NAME</th>
              <th>TYPE</th>
              <th className="center">ACTIVE</th>
              <th className="center">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={`${group.source}-${group.id}`}>
                <td className="group-name">
                  <strong>{group.name}</strong>
                </td>
                <td className="group-type">
                  <span className={`type-badge ${group.source} ${group.type?.toLowerCase()}`}>
                    {group.type || '-'}
                  </span>
                </td>
                <td className="group-active">
                  <span className={`status-badge ${group.active ? 'active' : 'inactive'}`}>
                    {group.active ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="group-actions">
                  <button className="btn-edit" onClick={() => handleEdit(group)}>
                    Edit
                  </button>
                  <button className="btn-delete-disabled" disabled>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="section-content">
      <div className="page-header">
        <h2>Groups</h2>
        <button className="btn-primary" onClick={() => setShowAddDialog(true)}>
          + Add Category Group
        </button>
      </div>

      {accountGroups.length === 0 && categoryGroups.length === 0 ? (
        <p className="empty-state">No category groups yet.</p>
      ) : (
        <div className="grouped-sections">
          {renderGroupTable(accountGroups, 'account')}
          {renderGroupTable(categoryGroups, 'category')}
        </div>
      )}

      <AddCategoryGroupDialog
        isOpen={showAddDialog}
        onClose={() => {
          setShowAddDialog(false);
          setEditingGroup(null);
        }}
        onSuccess={() => {
          // Groups will auto-reload via onSnapshot
        }}
        editGroup={editingGroup}
      />
    </div>
  );
}

export default CategoryGroups;